// src/feed/writeFeedItem.ts
//
// Single entry point for creating a feed_items doc. Centralized so:
//   • All callers go through the same defaults logic and visibility
//     resolver — no caller has to know the rules.
//   • Future schema changes (e.g. moderation queue, fan-out triggers)
//     hook in one place.
//   • The function returns the new id so callers can deep-link or
//     show a toast that links to the post.

import {
  collection,
  doc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db, auth } from '../services/firebase';
import {
  FeedItem,
  FeedItemKind,
  FeedItemSnapshot,
  FeedItemSource,
  FeedVisibility,
} from './types';
import { resolveVisibility } from './feedDefaults';

export interface WriteFeedItemInput {
  kind: FeedItemKind;
  /** Explicit visibility override. If absent, resolveVisibility()
   *  pulls from the user's preferences (or the hardcoded defaults). */
  visibility?: FeedVisibility;
  snapshot: FeedItemSnapshot;
  source: FeedItemSource;
}

/** Returns the new feed_item id, or null on failure (logged). */
export async function writeFeedItem(
  input: WriteFeedItemInput
): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) {
    console.warn('writeFeedItem: no authenticated user — skipping');
    return null;
  }

  const visibility =
    input.visibility ?? (await resolveVisibility(user.uid, input.kind));

  // Author snapshot. We pull from auth.currentUser so the data is
  // always consistent with what the user sees as "me" in the auth
  // shell. `displayName` may be null for users who haven't set one
  // (especially anonymous accounts) — fall back to a generic label
  // rather than rendering "null" or an empty string. `photoURL` is
  // optional; FeedItemCard's avatar falls back to a colored initial
  // when it's missing.
  const author: { display_name: string; photo_url?: string } = {
    display_name: user.displayName?.trim() || 'Anonymous traveler',
  };
  if (user.photoURL) {
    author.photo_url = user.photoURL;
  }

  const ref = doc(collection(db, 'feed_items'));
  const itemData: Omit<FeedItem, 'id' | 'created_at' | 'updated_at'> & {
    created_at: ReturnType<typeof serverTimestamp>;
    updated_at: ReturnType<typeof serverTimestamp>;
  } = {
    owner_id: user.uid,
    author,
    kind: input.kind,
    visibility,
    snapshot: stripUndefined(input.snapshot),
    source: stripUndefined(input.source),
    reactions_count: 0,
    comments_count: 0,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  };

  try {
    await setDoc(ref, itemData);
    return ref.id;
  } catch (err) {
    console.warn('writeFeedItem failed:', err);
    return null;
  }
}

// Firestore rejects `undefined` as a field value with
// "Unsupported field value: undefined". Callers may pass partial
// snapshots where optional fields are undefined; we recursively
// remove those keys before writing. (Null is fine; only undefined
// is rejected.)
function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value
      .filter((v) => v !== undefined)
      .map((v) => stripUndefined(v)) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(value as Record<string, any>)) {
      if (v === undefined) continue;
      out[k] = stripUndefined(v);
    }
    return out as T;
  }
  return value;
}
